<template>
  <div class="rx-sign">
    <!-- Assinada no Ares Sign: selo + baixar -->
    <template v-if="signed">
      <span
        class="badge badge-light-success rx-sign__badge"
        :title="signedTitle"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="12"
          class="mr-25"
        />
        {{ $t('involvedPanel.prescriptions.signature.signed') }}
      </span>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        :disabled="busy"
        @click="downloadSigned"
      >
        <feather-icon
          icon="DownloadIcon"
          size="12"
          class="mr-25"
        />
        {{ $t('involvedPanel.prescriptions.signature.downloadSigned') }}
      </button>
    </template>

    <!-- Já enviada ao Ares Sign: situação + PDF enviado + atualizar -->
    <template v-else-if="signature">
      <span
        class="badge rx-sign__badge"
        :class="`badge-light-${statusColor}`"
      >{{ statusLabel }}</span>
      <small
        v-if="signature.error"
        class="text-danger rx-sign__error"
      >{{ signature.error }}</small>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="busy"
        @click="openPdf"
      >
        <feather-icon
          icon="FileTextIcon"
          size="12"
          class="mr-25"
        />
        {{ $t('involvedPanel.prescriptions.signature.viewPdf') }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="busy"
        @click="sign"
      >
        <feather-icon
          icon="Edit3Icon"
          size="12"
          class="mr-25"
        />
        {{ $t('involvedPanel.prescriptions.signature.sign') }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="busy"
        :title="$t('involvedPanel.prescriptions.signature.refresh')"
        @click="refresh"
      >
        <b-spinner
          v-if="busy"
          small
        />
        <feather-icon
          v-else
          icon="RefreshCwIcon"
          size="12"
        />
      </button>
    </template>

    <!-- Ainda não enviada: PDF + enviar -->
    <template v-else>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="busy"
        @click="openPdf"
      >
        <feather-icon
          icon="FileTextIcon"
          size="12"
          class="mr-25"
        />
        {{ $t('involvedPanel.prescriptions.signature.generatePdf') }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="busy"
        @click="sign"
      >
        <b-spinner
          v-if="busy"
          small
          class="mr-25"
        />
        <feather-icon
          v-else
          icon="SendIcon"
          size="12"
          class="mr-25"
        />
        {{ $t('involvedPanel.prescriptions.signature.sign') }}
      </button>
    </template>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import AresSignPopupMixin from '@/mixins/AresSignPopupMixin'

const COLORS = {
  pending: 'secondary',
  awaiting_authorization: 'warning',
  signing: 'info',
  error: 'danger',
}

/**
 * Botões de assinatura digital de uma receita. "Assinar" envia o PDF ao Ares Sign
 * e abre a página dele numa janela: quem assina assina lá, e o Ares Sign devolve
 * o documento assinado (aviso de volta). A situação chega em tempo real;
 * "atualizar" consulta o Ares Sign quando o aviso não alcança a Despacho.
 */
export default {
  name: 'PrescriptionSignatureActions',
  components: { BSpinner },
  mixins: [ApiErrorMixin, AresSignPopupMixin],
  props: {
    batchId: { type: String, required: true },
    /** Resumo da assinatura (null = receita ainda não enviada). */
    signature: { type: Object, default: null },
  },
  data() {
    return { busy: false }
  },
  computed: {
    signed() {
      return !!this.signature && this.signature.status === 'signed'
    },
    statusColor() {
      return COLORS[this.signature && this.signature.status] || 'secondary'
    },
    statusLabel() {
      return this.$t(`involvedPanel.prescriptions.signature.status.${this.signature.status}`)
    },
    signedTitle() {
      const date = this.signature.signed_at
        ? new Date(this.signature.signed_at).toLocaleString('pt-BR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
        : '—'

      return this.$t('involvedPanel.prescriptions.signature.signedBy', {
        name: this.signature.signer_name || '—',
        date,
      })
    },
  },
  methods: {
    base() {
      return `${this.$store.state.api}occurrence_prescriptions/batch/${this.batchId}`
    },

    errorTitle() {
      return this.$t('involvedPanel.prescriptions.messages.errorTitle')
    },

    // PDF da receita: o que foi enviado ao Ares Sign, ou, antes disso, o gerado agora.
    async openPdf() {
      // Abre a aba já no clique; depois do await o bloqueador de pop-ups barraria.
      const win = window.open('', '_blank')
      this.busy = true
      try {
        const url = this.signature ? `${this.base()}/signature/download?versao=original` : `${this.base()}/pdf`
        const res = await this.$http.get(url, { responseType: 'blob' })
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        if (win) win.location = blobUrl
        else window.open(blobUrl)
      } catch (e) {
        if (win) win.close()
        this.$message(this.errorTitle(), this.$t('involvedPanel.prescriptions.signature.pdfError'), 'error')
      } finally {
        this.busy = false
      }
    },

    // Envia a receita ao Ares Sign (na primeira vez) e abre a página onde se assina.
    async sign() {
      if (!this.signature) {
        // Depois de enviada o PDF fica fixo: a receita não pode mais ser cancelada.
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t('involvedPanel.prescriptions.signature.confirm.text'),
          {
            title: this.$t('involvedPanel.prescriptions.signature.confirm.title'),
            okTitle: this.$t('involvedPanel.prescriptions.signature.confirm.ok'),
            cancelTitle: this.$t('involvedPanel.prescriptions.signature.confirm.cancel'),
            centered: true,
            size: 'sm',
          },
        )
        if (!confirmed) return
      }

      const win = this.openSignWindow()
      this.busy = true
      try {
        let signature = this.signature
        if (!signature) {
          const res = await this.$http.post(`${this.base()}/signature`)
          signature = res.data.data
          this.$emit('update:signature', signature)
        }

        if (win && signature.sign_url) {
          win.location = signature.sign_url
          this.watchSignWindow(win, this.refresh)
        } else {
          if (win) win.close()
          this.$message(this.errorTitle(), this.$t('involvedPanel.prescriptions.signature.popupBlocked'), 'warning')
        }
      } catch (e) {
        if (win) win.close()
        this.$message(this.errorTitle(), this.apiErrorText(e, this.$t('involvedPanel.prescriptions.signature.failed')), 'error')
      } finally {
        this.busy = false
      }
    },

    async refresh() {
      this.busy = true
      try {
        const res = await this.$http.get(`${this.base()}/signature`, { params: { refresh: 1 } })
        if (res.data.data) this.$emit('update:signature', res.data.data)
      } catch (e) {
        this.$message(this.errorTitle(), this.apiErrorText(e, this.$t('involvedPanel.prescriptions.signature.refreshError')), 'error')
      } finally {
        this.busy = false
      }
    },

    async downloadSigned() {
      this.busy = true
      try {
        const res = await this.$http.get(`${this.base()}/signature/download?versao=assinado`, { responseType: 'blob' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        link.download = 'receita-assinada.pdf'
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (e) {
        this.$message(this.errorTitle(), this.$t('involvedPanel.prescriptions.signature.downloadError'), 'error')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.rx-sign {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.rx-sign .btn {
  font-size: 11px;
  padding: 2px 8px;
}

.rx-sign__badge {
  font-size: 11px;
}

.rx-sign__error {
  max-width: 260px;
}
</style>
